import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {assert} from 'Asserts';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineStep} from 'pipelines/PipelineTypes';
import {JSONValue} from 'util/Types';

import {AnalysisConfigReference} from './AnalysisAdapterTypes';

/**
 * Given a config reference, return the step, config, and config data frame spec for it.
 */
export function resolveConfigReference(
  configReference: AnalysisConfigReference,
  layoutStep: PipelineStep<PipelineStepType.LAYOUT, 0>,
  analysis: AnalysisGraphQL
): {
  step: PipelineStep;
  config: JSONValue;
  configDataFrameId: string;
  configDataFrameSpec: DataFrameSpec;
} {
  const matchingIncomingEdge = layoutStep.incomingEdges.find(
    (incomingEdge) =>
      incomingEdge.toStepData.name === configReference.configDataName &&
      incomingEdge.toStepDataIndex === configReference.configDataIndex
  )!;

  const stepId = matchingIncomingEdge.fromStep.id;
  const step = analysis.steps.find(({id}) => id === stepId)!;

  const configPath = configReference.configPath;
  const config: JSONValue = configPath.reduce((currentConfig, configKey) => {
    if (Array.isArray(currentConfig)) {
      return currentConfig[configKey as number];
    } else if (typeof currentConfig === 'object' && currentConfig != null) {
      return (currentConfig as Record<string, JSONValue>)[configKey as string];
    }
    throw new Error(`Invalid path: ${configPath.join('.')}`);
  }, step.config);

  // If this assumption becomes problematic, we could remove it by making `configDataFrameSpec` optional,
  // and updating StepConfigEditorRouterV2 to handle the dataFrameSpec being optional. Mainly this means
  // we can't render column reference fields.
  assert(
    step.inputStepDatas.length > 0 &&
      step.inputStepDatas[0].rawDatas.length > 0 &&
      step.inputStepDatas[0].rawDatas[0].__typename === 'DataFrameDomainNode',
    'Config steps must have at least one input data frame'
  );

  return {
    step,
    config,
    configDataFrameId: step.inputStepDatas[0].rawDatas[0].id,
    configDataFrameSpec: step.inputStepDatas[0].rawDatas[0],
  };
}
